import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import ModalComponent from "components/modal-component";
import AddCurrencyRuleModal from "./modals/AddCurrencyRuleModal";
import CurrencyRuleHistoryModal from "./modals/CurrencyRuleHistoryModal";
import EditCurrencyRuleModal from "./modals/EditCurrencyRuleModal";
import "../../../../styles/modal.css";
import AuditTrailPage from "./AuditTrail";
import BulkImport from "./BulkImport";
import { showToastError } from "components/toastify/ToastNotifications";
import Pagination from "rc-pagination/lib/Pagination";
import DeleteCurrencyRuleModal from "./modals/DeleteCurrencyRuleModal";
import { useGetCurrencyRuleQuery } from "api/ruleEngineApi";
import { GoHistory } from "react-icons/go";
import DeleteIcon from "assets/svg-icon/rule_engine/delete";
import EditIcon from "assets/svg-icon/rule_engine/edit";
import ExportIcon from "assets/svg-icon/rule_engine/export";
import { IoAddSharp } from "react-icons/io5";

interface CurrencyRuleData {
  id: string;
  application: string;
  businessLine: string;
  region: string | null;
  sellCurrency: string;
  sellRate: number;
  costCurrency: string;
  costAmount: number;
  externalSupplier: string;
  externalSupplierCost: number;
  effectiveDate: string;
  status: string;
  createdBy: string;
  modifiedBy: string;
  createdAt: string;
  modifiedAt: string;
  versionCount: number;
}

interface CurrencyRuleResponse {
  totalPages: number;
  pageSize: number;
  pageNumber: number;
  totalSize: number;
  items: CurrencyRuleData[];
}

interface SubCompanyPagination {
  pageNumber: number;
  totalSize: number;
  pageSize: number;
}

const CurrencyAndCostPage = () => {
  const [loading, setLoading] = useState(false);
  const [currencyRules, setCurrencyRules] = useState<CurrencyRuleData[]>([]);
  const [activeView, setActiveView] = useState<
    "main" | "audit-trail" | "bulk-import"
  >("main");

  const [subCompanyPagination, setSubCompanyPagination] =
    useState<SubCompanyPagination>();
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [showEditCurrencyRuleModal, setShowEditCurrencyRuleModal] =
    useState(false);
  const [showCurrencyHistoryModal, setShowCurrencyHistoryModal] =
    useState(false);
  const [showDeleteCurrencyRuleModal, setShowDeleteCurrencyRuleModal] =
    useState(false);
  const [selectedRule, setSelectedRule] = useState<CurrencyRuleData | null>(
    null
  );

  const {
    data: currencyRuleData,
    error: currencyRuleError,
    isFetching: isFetchingCurrencyRule,
    refetch: refetchCurrencyRule,
  } = useGetCurrencyRuleQuery();

  useEffect(() => {
    if (isFetchingCurrencyRule) {
      setLoading(true);
    } else {
      setLoading(false);
      if (currencyRuleError) {
        showToastError("Unable to fetch data from the server");
      } else if (currencyRuleData?.data) {
        // Safe access to the items array
        const response = currencyRuleData.data as CurrencyRuleResponse;
        setCurrencyRules(response.items || []);

        // Set pagination info
        setSubCompanyPagination({
          pageNumber: response.pageNumber,
          totalSize: response.totalSize,
          pageSize: response.pageSize,
        });

        
      } else {
        // Handle case where data is null/undefined
        setCurrencyRules([]);
      }
    }
  }, [isFetchingCurrencyRule, currencyRuleData, currencyRuleError]);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "Active":
        return "#007bff";
      case "Draft":
        return "#ffc107";
      case "Expired":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return dateString.split("T")[0];
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString; // fallback to original if formatting fails
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      marginTop: "1rem",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1.5rem",
    },
    headerTitle: {
      fontWeight: "bold",
      fontSize: "1.25rem",
      margin: 0,
      color: "#2c3e50",
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
    },
    button: {
      padding: "0.375rem 0.75rem",
      fontSize: "0.875rem",
      border: "1px solid #ced4da",
      borderRadius: "0.25rem",
      backgroundColor: "white",
      color: "#495057",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    tableContainer: {
      backgroundColor: "white",
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      position: "relative",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "14px",
    },
    thead: {
      backgroundColor: "rgba(255, 255, 255, 0.5)",
    },
    th: {
      padding: "16px 20px",
      fontWeight: "500",
      fontSize: "13px",
      color: "#6c757d",
      border: "none",
      textAlign: "left",
      borderBottom: "1px solid #e9ecef",
    },
    td: {
      padding: "20px",
      fontSize: "14px",
      color: "#17171B",
      border: "none",
      verticalAlign: "middle",
      height: "115px",
    },
    tdRevenueLine: {
      padding: "20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#2c3e50",
      border: "none",
      verticalAlign: "middle",
    },
    row: {
      borderBottom: "1px solid #f1f3f4",
      backgroundColor: "white",
    },
    lastRow: {
      borderBottom: "none",
      backgroundColor: "white",
    },
    versionBadge: {
      color: "#17171B",
      padding: "6px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "500",
      border: "1px solid #17171B",
      display: "inline-block",
      width: "62px",
      textAlign: "center",
    },
    statusBadge: {
      padding: "6px 16px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "500",
      color: "white",
      display: "inline-block",
    },
    actionsContainer: {
      display: "flex",
    },
    actionButton: {
      padding: "8px",
      color: "#2992D32",
      backgroundColor: "transparent",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px",
    },
    costAmountContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    },
    externalCostValue: {
      fontSize: "12px",
      color: "#dc3545",
      fontWeight: "500",
    },
    externalLabel: {
      fontSize: "12px",
      fontWeight: "500",
      color: "#17171B",
      marginTop: "5px",
      backgroundColor: "#EFEFF4",
      borderRadius: "64px",
      padding: "6.5px 8.5px",
    },
    // Loading overlay styles
    loadingOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10,
    },
    loadingSpinner: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontSize: "14px",
      color: "#6c757d",
    },
    spinner: {
      width: "24px",
      height: "24px",
      border: "2px solid #f3f3f3",
      borderTop: "2px solid #007bff",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    // Empty state styles
    emptyState: {
      textAlign: "center",
      padding: "40px 20px",
      color: "#6c757d",
    },
    emptyStateIcon: {
      fontSize: "48px",
      marginBottom: "16px",
      color: "#dee2e6",
    },
    emptyStateText: {
      fontSize: "16px",
      fontWeight: "500",
      marginBottom: "8px",
    },
    emptyStateSubtext: {
      fontSize: "14px",
      color: "#adb5bd",
    },
  };

  const handleEditClick = (rule: CurrencyRuleData) => {
    setSelectedRule(rule);
    setShowEditCurrencyRuleModal(true);
  };

  const handleDeleteClick = (rule: CurrencyRuleData) => {
    setSelectedRule(rule);
    setShowDeleteCurrencyRuleModal(true);
  };

  const handleHistoryClick = (rule: CurrencyRuleData) => {
    setSelectedRule(rule);
    setShowCurrencyHistoryModal(true);
  };

  const handleBulkImportSuccess = () => {
    refetchCurrencyRule();
    // Go back to the main view
    setActiveView("main");
  };

  // Function to render the appropriate header based on active view
  const renderHeader = () => {
    switch (activeView) {
      case "audit-trail":
        return (
          <div className="page-header">
            <h5 className="page-title">Audit Trail</h5>
            <p className="page-subtitle">
              Track all changes to currency and cost rules with versioning
            </p>
            <div className="breadcrumb">
              <span className="breadcrumb-item">Bulk Escrow</span>
              <span className="breadcrumb-separator">{">"}</span>
              <span
                className="breadcrumb-item"
                style={{ cursor: "pointer", color: "#007bff" }}
                onClick={() => setActiveView("main")}
              >
                Currency & Cost Rules
              </span>
              <span className="breadcrumb-separator">{">"}</span>
              <span className="breadcrumb-item current">Audit Trail</span>
            </div>
          </div>
        );

      case "bulk-import":
        return (
          <div className="page-header">
            <h5 className="page-title">Bulk Import</h5>
            <p className="page-subtitle">
              Upload CSV file to batch update currency rules
            </p>
            <div className="breadcrumb">
              <span>Rule Engine</span>
              <span className="breadcrumb-separator">›</span>
              <span
                style={{ cursor: "pointer", color: "#007bff" }}
                onClick={() => setActiveView("main")}
              >
                Currency & Cost Rules
              </span>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-current">Bulk Import</span>
            </div>
          </div>
        );

      case "main":
      default:
        return (
          <div
            className="d-flex justify-content-between"
            style={{ marginTop: "56px" }}
          >
            <h5 className="fw-bold mb-0">Currency & Cost Rules</h5>
            <div className="d-flex gap-2 mb-4 page-buttons">
              <button
                style={{
                  border: "none",
                  padding: "10px 20px",
                  color: "#807E7C",
                  borderRadius: "12px",
                }}
                onClick={() => setActiveView("audit-trail")}
                disabled={loading}
              >
                Audit Trail
              </button>
              <button
                style={{
                  border: "none",
                  padding: "10px 20px",
                  color: "#807E7C",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onClick={() => setActiveView("bulk-import")}
                disabled={loading}
              >
                <ExportIcon />
                Bulk Import
              </button>
              <button
                style={{
                  border: "none",
                  padding: "10px 20px",
                  color: "#807E7C",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onClick={() => setShowAddRuleModal(true)}
                disabled={loading}
              >
                <IoAddSharp />
                Add Rule
              </button>
            </div>
          </div>
        );
    }
  };

  const CurrencyAndCost = () => (
    <div style={styles.container}>
      <div style={styles.tableContainer}>
        {/* Loading overlay */}
        {loading && (
          <div className="p-5 text-center">
            <Spinner />
          </div>
        )}

        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <th style={styles.th}>S/N</th>
              <th style={styles.th}>Revenue Line</th>
              <th style={styles.th}>Region</th>
              <th style={styles.th}>Sell Currency</th>
              <th style={styles.th}>Sell Rate</th>
              <th style={styles.th}>Cost Currency</th>
              <th style={styles.th}>Cost Amount</th>
              <th style={styles.th}>External Cost</th>
              <th style={styles.th}>Version</th>
              <th style={styles.th}>Effective Date</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && currencyRules.length === 0 ? (
              <tr>
                <td colSpan={11} style={styles.emptyState}>
                  <div style={styles.emptyStateIcon}>📊</div>
                  <div style={styles.emptyStateText}>
                    No currency rules found
                  </div>
                  <div style={styles.emptyStateSubtext}>
                    Add your first currency rule to get started
                  </div>
                </td>
              </tr>
            ) : (
              currencyRules.map((data, index) => (
                <tr
                  key={data.id}
                  style={
                    index === currencyRules?.length - 1
                      ? styles.lastRow
                      : styles.row
                  }
                >
                  <td style={styles.tdRevenueLine}>{index + 1}</td>
                  <td style={styles.tdRevenueLine}>{data.businessLine}</td>
                  <td style={styles.td}>{data.region || "N/A"}</td>
                  <td style={styles.td}>{data.sellCurrency}</td>
                  <td style={styles.td}>{data.sellRate}</td>
                  <td style={styles.td}>{data.costCurrency}</td>
                  <td style={styles.td}>
                    {data.externalSupplier === "External" ? (
                      <div style={styles.costAmountContainer}>
                        <span>{data.costAmount}</span>
                        <span style={styles.externalLabel}>External</span>
                      </div>
                    ) : (
                      data.costAmount
                    )}
                  </td>
                  <td style={styles.td}>
                    {data.externalSupplier === "External" ? (
                      <div style={styles.costAmountContainer}>
                        <span style={styles.externalCostValue}>
                          {data.externalSupplierCost}
                        </span>
                      </div>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.versionBadge}>
                      v{data.versionCount}
                    </span>
                  </td>
                  <td style={styles.td}>{formatDate(data.effectiveDate)}</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: getStatusColor(data.status),
                      }}
                    >
                      {data.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ ...styles.td, paddingRight: "20px" }}>
                      <button
                        style={styles.actionButton}
                        title="Edit"
                        onClick={() => handleEditClick(data)}
                        disabled={loading}
                      >
                        <EditIcon />
                      </button>
                      <button
                        style={styles.actionButton}
                        title="Delete"
                        onClick={() => handleDeleteClick(data)}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </button>
                      <button
                        style={styles.actionButton}
                        title="History"
                        onClick={() => handleHistoryClick(data)}
                        disabled={loading}
                      >
                        <GoHistory />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ModalComponent
        showModal={showCurrencyHistoryModal}
        hideModal={() => setShowCurrencyHistoryModal(false)}
        dialogClassName="rule-history-modal"
      >
        <CurrencyRuleHistoryModal
          activeModalTab="add-user"
          onClose={() => setShowCurrencyHistoryModal(false)}
          ruleId={selectedRule?.id}
        />
      </ModalComponent>

      <ModalComponent
        showModal={showEditCurrencyRuleModal}
        hideModal={() => setShowEditCurrencyRuleModal(false)}
        dialogClassName="add-currency-rule-modal"
      >
        <EditCurrencyRuleModal
          activeModalTab="add-user"
          onClose={() => setShowEditCurrencyRuleModal(false)}
          selectedRule={selectedRule}
          onSuccess={() => {
            refetchCurrencyRule();
            setShowEditCurrencyRuleModal(false);
          }}
        />
      </ModalComponent>

      <ModalComponent
        showModal={showDeleteCurrencyRuleModal}
        hideModal={() => setShowDeleteCurrencyRuleModal(false)}
        dialogClassName="add-currency-rule-modal"
      >
        <DeleteCurrencyRuleModal
          show={showDeleteCurrencyRuleModal}
          onClose={() => setShowDeleteCurrencyRuleModal(false)}
          onDelete={() => {
            refetchCurrencyRule();
            setShowDeleteCurrencyRuleModal(false);
          }}
          ruleName={selectedRule?.businessLine}
          ruleId={selectedRule?.id}
        />
      </ModalComponent>

      {!loading && (
        <div
          style={{
            marginTop: "25px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Pagination
            current={subCompanyPagination?.pageNumber || 1}
            total={subCompanyPagination?.totalSize || 0}
            pageSize={subCompanyPagination?.pageSize || 10}
            onChange={onPageChange}
          />
        </div>
      )}
    </div>
  );

  const onPageChange = (page: number) => {
    // Update the page number in state
    setSubCompanyPagination((prev) =>
      prev ? { ...prev, pageNumber: page } : undefined
    );

    // If you need to refetch data for the new page, call your API here
    // refetchCurrencyRule({ page }); // Adjust based on your API requirements
  };

  return (
    <div>
      {loading ? (
        <div className="p-5 text-center">
          <Spinner />
        </div>
      ) : (
        <>
          {/* Conditional header rendering based on active view */}
          {renderHeader()}

          {/* Back to Main button for non-main views */}
          {/* {activeView !== "main" && (
            <div className="mb-3">
              <Button
                variant="outline-secondary"
                onClick={() => setActiveView("main")}
                disabled={loading}
              >
                ← Back to Currency & Cost Rules
              </Button>
            </div>
          )} */}

          <div>
            {activeView === "main" && <CurrencyAndCost />}
            {activeView === "audit-trail" && <AuditTrailPage />}
            {activeView === "bulk-import" && (
              <BulkImport
                onClose={() => setActiveView("main")}
                onSuccess={handleBulkImportSuccess}
              />
            )}
          </div>

          {/* Add Rule Modal - only show on main view */}
          {activeView === "main" && (
            <ModalComponent
              showModal={showAddRuleModal}
              hideModal={() => setShowAddRuleModal(false)}
              dialogClassName="add-currency-rule-modal"
            >
              <AddCurrencyRuleModal
                activeModalTab="add-user"
                onClose={() => setShowAddRuleModal(false)}
                onSuccess={() => {
                  refetchCurrencyRule();
                  setShowAddRuleModal(false);
                }}
              />
            </ModalComponent>
          )}

          <style>
            {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            .breadcrumb-item {
              cursor: default;
            }
            
            .breadcrumb-item:hover {
              text-decoration: underline;
            }
          `}
          </style>
        </>
      )}
    </div>
  );
};

export default CurrencyAndCostPage;
